import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  styled,
  Typography,
} from "@mui/material";
import Button from "@components/Button/Button";
import FormDivider from "@components/FormDivider/FormDivider";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import { type NormalGatepassFormData } from "@features/gatepass/schema/normalGatepassFormSchema";
import { formatOnChangeEmiratesIDNumber } from "@features/port-security/components/utils";
import { checkGatepassSecurityClearance } from "@services/securityClearanceApi";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { runInquiry } from "@telemetry/runInquiry";
import { paths } from "@utils/navigation";
import { t } from "i18next";
import {
  Controller,
  FormProvider,
  useFieldArray,
  type FieldErrors,
  type UseFormReturn,
} from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { useNormalGatepassFormStore } from "../../store/useNormalGatepassForm.store";
import { createScIdValidators, extractScErrorDetail, resolveScSearchField } from "../utils";
import SecurityClearanceErrorMessage from "./SecurityClearanceErrorMessage";
import SecurityClearanceStatusMessage from "./SecurityClearanceStatusMessage";

export interface FormContext {
  formData: UseFormReturn<NormalGatepassFormData>;
  updateFormData: (key: string, value: unknown) => void;
}

const TRANSLATION_KEY = "gatepass";
const gatepassService = "bff-gatepass";
const SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT = {
  module: "gatepass",
  screen: "Normal Gatepass Group Members",
  action: "check_group_member_security_clearance",
  entityType: "security_clearance",
};
const SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT = {
  module: "gatepass",
  screen: "Normal Gatepass Group Members",
  action: "populate_group_member_security_clearance",
  fromField: "groupMembers.documentId",
  toField: "groupMembers.securityClearance",
  entityType: "security_clearance",
};

const EMPTY_ARRAY: unknown[] = [];

type GroupMemberRow = {
  passportNumber: string;
  emiratesIdNumber: string;
  fullName: string;
  nationality?: string;
  passportExpiryDate?: string;
  phoneNumber?: string;
  securityClearanceId?: string;
  securityClearanceStatus?: string;
  securityClearanceSerial?: string;
  securityClearanceValidity?: string;
  securityClearanceFott?: string;
};

const RemoveButton = styled(Button)(() => ({
  "color": "#333333",
  "backgroundColor": "#F1F2F4",
  "&:hover": {
    backgroundColor: "#F1F2F4",
  },
}));

const createBlankMember = (): GroupMemberRow => ({
  passportNumber: "",
  emiratesIdNumber: "",
  fullName: "",
  nationality: "",
  passportExpiryDate: "",
  phoneNumber: "",
  securityClearanceId: "",
  securityClearanceStatus: "",
  securityClearanceSerial: "",
  securityClearanceValidity: "",
  securityClearanceFott: "",
});

const formatDateToDdMmYyyy = (value?: string | Date | null): string => {
  if (!value) return "";
  const parsedDate = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "";
  const day = String(parsedDate.getDate()).padStart(2, "0");
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const year = parsedDate.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatEmiratesIdValue = (value: string) => {
  const event = {
    target: { value },
  } as unknown as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;

  formatOnChangeEmiratesIDNumber(event);
  return event.target.value;
};

const GroupMembers = () => {
  const fottDisplayOptions = [
    { label: t("common.yes"), value: t("yes") },
    { label: t("common.no"), value: t("no") },
  ];
  const { formData, updateFormData } = useOutletContext<FormContext>();
  const { setField } = useNormalGatepassFormStore();
  const gatepassType = formData.watch("gatepassType");
  const members = formData.watch("groupMembers") ?? (EMPTY_ARRAY as GroupMemberRow[]);
  const activeLookupKeyRef = useRef<Record<number, string>>({});

  const getLastIdValues = useCallback(
    () =>
      (formData.getValues("groupMembers") ?? EMPTY_ARRAY).map((member) => ({
        passportNumber: member.passportNumber,
        emiratesIdNumber: member.emiratesIdNumber,
      })),
    [formData],
  );
  const lastIdValueRef = useRef(getLastIdValues());

  const [isCheckingByRow, setIsCheckingByRow] = useState<Record<number, boolean>>({});
  const [lastSearchFieldByRow, setLastSearchFieldByRow] = useState<
    Record<number, "passport" | "emirates" | null>
  >({});
  const [scCheckErrorMessageByRow, setScCheckErrorMessageByRow] = useState<Record<number, string>>(
    {},
  );

  const { fields, append, remove } = useFieldArray({
    control: formData.control,
    name: "groupMembers",
  });

  const { isPassportValid, isEmiratesIdValid } = useMemo(
    () => createScIdValidators(TRANSLATION_KEY, t),
    [],
  );

  const syncExternalMembers = useCallback(
    (nextMembers: GroupMemberRow[]) => {
      setField("groupMembers", nextMembers as NormalGatepassFormData["groupMembers"]);
      updateFormData("groupMembers", nextMembers);
    },
    [setField, updateFormData],
  );

  const syncMembers = useCallback(
    (nextMembers: GroupMemberRow[], shouldValidate = true) => {
      formData.setValue("groupMembers", nextMembers as NormalGatepassFormData["groupMembers"], {
        shouldDirty: true,
        shouldValidate,
      });
      lastIdValueRef.current = getLastIdValues();
      syncExternalMembers(nextMembers);
    },
    [formData, syncExternalMembers],
  );

  const resetRowScState = useCallback((rowIndex: number) => {
    setIsCheckingByRow((prev) => {
      if (!prev[rowIndex]) return prev;
      return { ...prev, [rowIndex]: false };
    });
    setLastSearchFieldByRow((prev) => {
      if (!prev[rowIndex]) return prev;
      return { ...prev, [rowIndex]: null };
    });
    setScCheckErrorMessageByRow((prev) => {
      if (!prev[rowIndex]) return prev;
      return { ...prev, [rowIndex]: "" };
    });
  }, []);

  const remapStateAfterRemoval = useCallback((removedIndex: number) => {
    const remap = <T,>(state: Record<number, T>) => {
      const next: Record<number, T> = {};
      Object.entries(state).forEach(([key, value]) => {
        const index = Number(key);
        if (index < removedIndex) {
          next[index] = value;
        }
        if (index > removedIndex) {
          next[index - 1] = value;
        }
      });
      return next;
    };

    setIsCheckingByRow((prev) => remap(prev));
    setLastSearchFieldByRow((prev) => remap(prev));
    setScCheckErrorMessageByRow((prev) => remap(prev));
  }, []);

  const checkMemberSecurityClearance = useCallback(
    async (rowIndex: number) => {
      const duplicateScMessage = t(`${TRANSLATION_KEY}.notifications.group-member-already-added`);
      const membersSnapshot = ((formData.getValues(
        "groupMembers",
      ) as NormalGatepassFormData["groupMembers"]) || []) as GroupMemberRow[];
      const member = membersSnapshot[rowIndex];
      if (!member) {
        return;
      }

      const passportNumber = member.passportNumber?.trim() ?? "";
      const emiratesIdNumber = member.emiratesIdNumber?.trim() ?? "";

      const hasValidPassport = isPassportValid(passportNumber);
      const hasValidEmiratesId = isEmiratesIdValid(emiratesIdNumber);

      if (!hasValidPassport && !hasValidEmiratesId) {
        return;
      }

      const lookupKey = `${passportNumber}|${emiratesIdNumber}`;
      activeLookupKeyRef.current[rowIndex] = lookupKey;

      setScCheckErrorMessageByRow((prev) => ({ ...prev, [rowIndex]: "" }));
      setIsCheckingByRow((prev) => ({ ...prev, [rowIndex]: true }));

      try {
        const response = await runFieldNavigation(SECURITY_CLEARANCE_FIELD_NAVIGATION_CONTEXT, () =>
          runInquiry(SECURITY_CLEARANCE_CHECK_INQUIRY_CONTEXT, () =>
            checkGatepassSecurityClearance({
              service: gatepassService,
              ...(hasValidPassport ? { passportNumber } : {}),
              ...(hasValidEmiratesId ? { emiratesId: emiratesIdNumber } : {}),
            }),
          ),
        );

        const hasScRecord = Boolean(response?.status || response?.securityClearanceId);

        const latestMembers = ((formData.getValues(
          "groupMembers",
        ) as NormalGatepassFormData["groupMembers"]) || []) as GroupMemberRow[];
        const latestMember = latestMembers[rowIndex];

        if (!latestMember) {
          return;
        }

        const latestLookupKey = `${latestMember.passportNumber?.trim() ?? ""}|${latestMember.emiratesIdNumber?.trim() ?? ""}`;
        if (activeLookupKeyRef.current[rowIndex] !== lookupKey || latestLookupKey !== lookupKey) {
          return;
        }

        const next = [...membersSnapshot];
        const current = next[rowIndex] ?? {};

        if (!hasScRecord) {
          next[rowIndex] = {
            ...current,
            nationality: "",
            passportExpiryDate: "",
            phoneNumber: "",
            securityClearanceId: "",
            securityClearanceStatus: "NOT_FOUND",
            securityClearanceSerial: "",
            securityClearanceValidity: "",
            securityClearanceFott: "",
          };
          syncMembers(next, true);
          setLastSearchFieldByRow((prev) => ({
            ...prev,
            [rowIndex]: resolveScSearchField(passportNumber, emiratesIdNumber),
          }));
          return;
        }

        const foundSecurityClearanceId = String(
          response.securityClearanceId ?? response.id ?? "",
        ).trim();
        const responsePassportNumber = String(
          response.passportNumber ?? passportNumber ?? "",
        ).trim();
        const responseEmiratesIdNumber = String(
          response.emiratesIdNumber ?? emiratesIdNumber ?? "",
        ).trim();
        const visitorSecurityClearanceId = String(
          formData.getValues("securityClearanceId") ?? "",
        ).trim();
        const visitorPassportNumber = String(formData.getValues("passportNumber") ?? "").trim();
        const visitorEmiratesIdNumber = String(formData.getValues("emiratesIdNumber") ?? "").trim();
        const isDuplicateWithVisitor = foundSecurityClearanceId
          ? foundSecurityClearanceId === visitorSecurityClearanceId
          : (responsePassportNumber && responsePassportNumber === visitorPassportNumber) ||
            (responseEmiratesIdNumber && responseEmiratesIdNumber === visitorEmiratesIdNumber);
        const isDuplicateWithOtherMember = latestMembers.some((groupMember, index) => {
          if (index === rowIndex) {
            return false;
          }

          const memberScId = String(
            (groupMember as GroupMemberRow)?.securityClearanceId ?? "",
          ).trim();
          const memberPassport = String(
            (groupMember as GroupMemberRow)?.passportNumber ?? "",
          ).trim();
          const memberEmiratesId = String(
            (groupMember as GroupMemberRow)?.emiratesIdNumber ?? "",
          ).trim();

          if (foundSecurityClearanceId && memberScId) {
            return memberScId === foundSecurityClearanceId;
          }

          return Boolean(
            (responsePassportNumber &&
              memberPassport &&
              memberPassport === responsePassportNumber) ||
              (responseEmiratesIdNumber &&
                memberEmiratesId &&
                memberEmiratesId === responseEmiratesIdNumber),
          );
        });

        if (isDuplicateWithVisitor || isDuplicateWithOtherMember) {
          next[rowIndex] = {
            ...current,
            nationality: "",
            passportExpiryDate: "",
            phoneNumber: "",
            securityClearanceId: "",
            securityClearanceStatus: "",
            securityClearanceSerial: "",
            securityClearanceValidity: "",
            securityClearanceFott: "",
          };
          syncMembers(next, true);
          formData.setError(`groupMembers.${rowIndex}.securityClearanceId`, {
            type: "manual",
            message: duplicateScMessage,
          });
          return;
        }

        next[rowIndex] = {
          ...current,
          securityClearanceId: foundSecurityClearanceId,
          securityClearanceStatus: response.status || "NOT_FOUND",
          securityClearanceSerial: String(response.serialNo ?? response.serialNumber ?? ""),
          securityClearanceValidity: formatDateToDdMmYyyy(response.expiryDate),
          securityClearanceFott: response.fott === true ? t("yes") : t("no"),
          fullName: current.fullName?.trim() ? current.fullName : (response.fullNameEnglish ?? ""),
          nationality: response.presentNationality ?? "",
          passportExpiryDate: response.passportExpiryDate || "",
          phoneNumber: response.mobileNumber ?? "",
          passportNumber:
            current.passportNumber?.trim() || !responsePassportNumber
              ? current.passportNumber
              : responsePassportNumber,
          emiratesIdNumber:
            current.emiratesIdNumber?.trim() || !responseEmiratesIdNumber
              ? current.emiratesIdNumber
              : responseEmiratesIdNumber,
        };

        syncMembers(next, true);
        formData.clearErrors([
          `groupMembers.${rowIndex}.passportNumber`,
          `groupMembers.${rowIndex}.emiratesIdNumber`,
          `groupMembers.${rowIndex}.securityClearanceId`,
        ]);
        void formData.trigger(`groupMembers.${rowIndex}`);
        setLastSearchFieldByRow((prev) => ({
          ...prev,
          [rowIndex]: resolveScSearchField(passportNumber, emiratesIdNumber),
        }));
        setScCheckErrorMessageByRow((prev) => ({ ...prev, [rowIndex]: "" }));
      } catch (_error) {
        const latestMembers = ((formData.getValues(
          "groupMembers",
        ) as NormalGatepassFormData["groupMembers"]) || []) as GroupMemberRow[];
        if (!latestMembers[rowIndex]) {
          return;
        }

        const errorDetail = extractScErrorDetail(_error);

        const next = [...latestMembers];
        next[rowIndex] = {
          ...next[rowIndex],
          nationality: "",
          passportExpiryDate: "",
          phoneNumber: "",
          securityClearanceId: "",
          securityClearanceStatus: "",
          securityClearanceSerial: "",
          securityClearanceValidity: "",
          securityClearanceFott: "",
        };
        syncMembers(next, true);
        setLastSearchFieldByRow((prev) => ({ ...prev, [rowIndex]: null }));
        setScCheckErrorMessageByRow((prev) => ({ ...prev, [rowIndex]: errorDetail }));
      } finally {
        setIsCheckingByRow((prev) => ({ ...prev, [rowIndex]: false }));
      }
    },
    [formData, isEmiratesIdValid, isPassportValid, syncMembers],
  );

  const renderCheckMessage = useCallback(
    (rowIndex: number) => {
      const isCheckingPassport = Boolean(isCheckingByRow[rowIndex]);
      const member = members[rowIndex];
      const scStatus = String(member?.securityClearanceStatus ?? "").trim();
      const securityClearanceSerial = String(member?.securityClearanceSerial ?? "").trim();
      const lastSearchField = lastSearchFieldByRow[rowIndex] ?? null;

      const securityClearanceId = String(member?.securityClearanceId ?? "").trim();
      const handleSerialClick = () => {
        if (!securityClearanceId) {
          return;
        }

        const url = `/${paths.portSecurity.index}/${paths.portSecurity.securityClearanceRequestPreview}/${securityClearanceId}`;
        window.open(url, "_blank");
      };

      return (
        <SecurityClearanceStatusMessage
          isChecking={isCheckingPassport}
          status={scStatus}
          serial={securityClearanceSerial}
          errorMessage={scCheckErrorMessageByRow[rowIndex]}
          lastSearchField={lastSearchField}
          onSerialClick={handleSerialClick}
          translationKey={TRANSLATION_KEY}
        />
      );
    },
    [isCheckingByRow, lastSearchFieldByRow, members, scCheckErrorMessageByRow],
  );

  const canRunManualScCheck = useCallback(
    (rowIndex: number) => {
      const member = members[rowIndex];
      if (!member) {
        return false;
      }

      const hasValidPassport = isPassportValid(member.passportNumber ?? "");
      const hasValidEmiratesId = isEmiratesIdValid(member.emiratesIdNumber ?? "");

      return !isCheckingByRow[rowIndex] && (hasValidPassport || hasValidEmiratesId);
    },
    [isCheckingByRow, isEmiratesIdValid, isPassportValid, members],
  );

  const handleMemberIdChange = useCallback(
    (
      rowIndex: number,
      fieldName: "passportNumber" | "emiratesIdNumber",
      rawValue: string,
      applyValue?: (value: string) => void,
    ) => {
      const value = fieldName === "emiratesIdNumber" ? formatEmiratesIdValue(rawValue) : rawValue;
      const currentFieldPath = `groupMembers.${rowIndex}.${fieldName}` as const;
      const previousValue = String(lastIdValueRef.current[rowIndex][fieldName] ?? "").trim();
      const hasChanged = previousValue !== value.trim();

      applyValue?.(value);
      void formData.trigger(currentFieldPath);

      if (hasChanged) {
        delete activeLookupKeyRef.current[rowIndex];

        const oppositeFieldName =
          fieldName === "passportNumber" ? "emiratesIdNumber" : "passportNumber";
        const oppositeFieldPath = `groupMembers.${rowIndex}.${oppositeFieldName}` as const;
        const oppositeValue = String(formData.getValues(oppositeFieldPath) ?? "").trim();

        if (value.trim() && oppositeValue) {
          formData.setValue(oppositeFieldPath, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
          void formData.trigger(oppositeFieldPath);
        }

        const fullNamePath = `groupMembers.${rowIndex}.fullName` as const;
        const fullNameValue = String(formData.getValues(fullNamePath) ?? "").trim();
        if (fullNameValue) {
          formData.setValue(fullNamePath, "", {
            shouldDirty: true,
            shouldValidate: true,
          });
          void formData.trigger(fullNamePath);
        }

        const nationalityPath = `groupMembers.${rowIndex}.nationality` as const;
        const nationalityValue = String(formData.getValues(nationalityPath) ?? "").trim();
        if (nationalityValue) {
          formData.setValue(nationalityPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const passportExpiryDatePath = `groupMembers.${rowIndex}.passportExpiryDate` as const;
        const passportExpiryDateValue = String(
          formData.getValues(passportExpiryDatePath) ?? "",
        ).trim();
        if (passportExpiryDateValue) {
          formData.setValue(passportExpiryDatePath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const phoneNumberPath = `groupMembers.${rowIndex}.phoneNumber` as const;
        const phoneNumberValue = String(formData.getValues(phoneNumberPath) ?? "").trim();
        if (phoneNumberValue) {
          formData.setValue(phoneNumberPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const securityClearanceIdPath = `groupMembers.${rowIndex}.securityClearanceId` as const;
        const securityClearanceIdValue = String(
          formData.getValues(securityClearanceIdPath) ?? "",
        ).trim();
        if (securityClearanceIdValue) {
          formData.setValue(securityClearanceIdPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const securityClearanceStatusPath =
          `groupMembers.${rowIndex}.securityClearanceStatus` as const;
        const securityClearanceStatusValue = String(
          formData.getValues(securityClearanceStatusPath) ?? "",
        ).trim();
        if (securityClearanceStatusValue) {
          formData.setValue(securityClearanceStatusPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const securityClearanceSerialPath =
          `groupMembers.${rowIndex}.securityClearanceSerial` as const;
        const securityClearanceSerialValue = String(
          formData.getValues(securityClearanceSerialPath) ?? "",
        ).trim();
        if (securityClearanceSerialValue) {
          formData.setValue(securityClearanceSerialPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const securityClearanceValidityPath =
          `groupMembers.${rowIndex}.securityClearanceValidity` as const;
        if (String(formData.getValues(securityClearanceValidityPath) ?? "").trim()) {
          formData.setValue(securityClearanceValidityPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        const securityClearanceFottPath = `groupMembers.${rowIndex}.securityClearanceFott` as const;
        if (String(formData.getValues(securityClearanceFottPath) ?? "").trim()) {
          formData.setValue(securityClearanceFottPath, "", {
            shouldDirty: true,
            shouldValidate: false,
          });
        }

        resetRowScState(rowIndex);
        formData.clearErrors([
          "securityClearanceValidity",
          `groupMembers.${rowIndex}.securityClearanceId`,
          `groupMembers.${rowIndex}.securityClearanceValidity`,
        ]);
      }

      lastIdValueRef.current = getLastIdValues();
      const nextMembers = (formData.getValues("groupMembers") as GroupMemberRow[]) || [];
      syncExternalMembers(nextMembers);
    },
    [formData, resetRowScState, syncExternalMembers],
  );

  useEffect(() => {
    if (gatepassType !== "group") {
      return;
    }

    if (fields.length > 0) {
      return;
    }

    const blank = createBlankMember();
    append(blank);
    syncMembers([blank], false);
  }, [append, fields.length, syncMembers, gatepassType]);

  if (gatepassType !== "group") {
    return null;
  }

  return (
    <FormProvider {...formData}>
      <FormControl fullWidth>
        <FormSection
          title={t(`${TRANSLATION_KEY}.group-members`)}
          isFullWidth
          button={
            <Button
              testId="group-members-add-button"
              startIcon={<AddIcon />}
              type="button"
              variant="outlined"
              label={
                <span className="normal-case font-medium">
                  {t(`${TRANSLATION_KEY}.add-group-member`)}
                </span>
              }
              onClick={() => {
                const blank = createBlankMember();
                const currentMembers =
                  (formData.getValues("groupMembers") as GroupMemberRow[]) || [];
                const nextMembers = [...currentMembers, blank];
                append(blank);
                syncMembers(nextMembers, true);
              }}
            />
          }
        >
          <div className="space-y-4 col-span-2">
            {fields.map((field, idx) => {
              const memberErrors = (formData.formState.errors?.groupMembers as FieldErrors[])?.[
                idx
              ] as FieldErrors | undefined;
              const memberScValidityError = memberErrors?.securityClearanceValidity?.message;

              const titleVisible = idx > 0 || fields.length > 1;

              return (
                <div key={field.id} className="space-y-4">
                  {titleVisible && (
                    <>
                      {idx > 0 && <FormDivider />}
                      <div className="col-span-2 pt-4">
                        <div className="flex justify-between">
                          <Typography variant="body2">
                            {t(`${TRANSLATION_KEY}.group-member-entry`, { number: idx + 1 })}
                          </Typography>
                          {fields.length > 1 && (
                            <RemoveButton
                              testId={`group-members-remove-${idx}`}
                              startIcon={<CloseIcon />}
                              type="button"
                              disableElevation
                              variant="contained"
                              label={<span className="normal-case font-medium">Remove</span>}
                              onClick={() => {
                                const currentMembers =
                                  (formData.getValues("groupMembers") as GroupMemberRow[]) || [];
                                const nextMembers = currentMembers.filter(
                                  (_, rowIndex) => rowIndex !== idx,
                                );
                                remove(idx);
                                remapStateAfterRemoval(idx);
                                syncMembers(nextMembers, true);
                              }}
                            />
                          )}
                        </div>
                        {memberScValidityError && (
                          <SecurityClearanceErrorMessage
                            message={String(memberScValidityError)}
                            data-testid={`group-member-scValidityError-${idx}`}
                          />
                        )}
                      </div>
                    </>
                  )}
                  {!titleVisible && memberScValidityError && (
                    <div className="col-span-2 pt-4">
                      <SecurityClearanceErrorMessage
                        message={String(memberScValidityError)}
                        data-testid={`group-member-scValidityError-${idx}`}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormItem<NormalGatepassFormData>
                      name={`groupMembers.${idx}.passportNumber` as const}
                      type="text"
                      label={t(`${TRANSLATION_KEY}.passport-number`)}
                      fullWidth
                      testId={`groupMembers-passportNumber-${idx}`}
                      onFieldChange={(_: string, value: unknown) => {
                        handleMemberIdChange(idx, "passportNumber", String(value));
                      }}
                      enforceUppercase
                    />

                    <div>
                      <CustomFormLabel
                        data-testid={`emiratesIdNumber-${idx}`}
                        sx={{ mb: 1, display: "block" }}
                      >
                        {t(`${TRANSLATION_KEY}.emirates-id-number`)}
                      </CustomFormLabel>
                      <Controller
                        name={`groupMembers.${idx}.emiratesIdNumber` as const}
                        control={formData.control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            fullWidth
                            testId={`groupMembers-emiratesIdNumber-${idx}`}
                            placeholder={t(`${TRANSLATION_KEY}.emirates-id-number`)}
                            error={Boolean(memberErrors?.emiratesIdNumber)}
                            onChange={(e) => {
                              handleMemberIdChange(
                                idx,
                                "emiratesIdNumber",
                                e.target.value,
                                inputField.onChange,
                              );
                            }}
                          />
                        )}
                      />
                      {memberErrors?.emiratesIdNumber?.message && (
                        <FormHelperText error sx={{ ml: 0 }}>
                          {String(memberErrors.emiratesIdNumber.message)}
                        </FormHelperText>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      {renderCheckMessage(idx)}
                      {memberErrors?.securityClearanceId?.message && (
                        <SecurityClearanceErrorMessage
                          message={String(memberErrors.securityClearanceId.message)}
                        />
                      )}
                      <Button
                        testId={`group-member-checkSc-${idx}`}
                        variant="outlined"
                        color="primary"
                        className="bg-white"
                        sx={{ textTransform: "none" }}
                        disabled={!canRunManualScCheck(idx)}
                        onClick={() => {
                          void checkMemberSecurityClearance(idx);
                        }}
                        label={t(`${TRANSLATION_KEY}.check-security-clearance`)}
                      />
                    </div>
                    <div className="md:col-span-2 flex flex-col gap-6 pb-7 md:flex-row">
                      <div className="flex min-w-0 flex-1 flex-col">
                        <CustomFormLabel sx={{ mb: 1 }} disabled>
                          {t("gatepass.security-clearance-validity", {
                            defaultValue: "Security clearance validity",
                          })}
                        </CustomFormLabel>
                        <div className="mt-auto w-full">
                          <Controller
                            name={`groupMembers.${idx}.securityClearanceValidity` as const}
                            control={formData.control}
                            render={({ field: inputField }) => (
                              <Input
                                {...inputField}
                                fullWidth
                                disabled
                                testId={`groupMembers-securityClearanceValidity-${idx}`}
                                placeholder="-"
                              />
                            )}
                          />
                        </div>
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <CustomFormLabel sx={{ mb: 1 }} disabled>
                          {t("gatepass.fott", { defaultValue: "FOTT" })}
                        </CustomFormLabel>
                        <div className="mt-auto w-full">
                          <Controller
                            name={`groupMembers.${idx}.securityClearanceFott` as const}
                            control={formData.control}
                            render={({ field: inputField }) => (
                              <RadioGroup
                                row
                                {...inputField}
                                value={inputField.value ?? ""}
                                onChange={(e) => inputField.onChange(e.target.value)}
                                sx={{
                                  "display": "flex",
                                  "flexDirection": "row",
                                  "flexWrap": "wrap",
                                  "columnGap": 2,
                                  "rowGap": 0.5,
                                  ".MuiFormControlLabel-root": {
                                    marginRight: 0,
                                  },
                                }}
                              >
                                {fottDisplayOptions.map((option) => (
                                  <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                    disabled
                                  />
                                ))}
                              </RadioGroup>
                            )}
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <CustomFormLabel
                        data-testid={`fullName-${idx}`}
                        sx={{ mb: 1, display: "block" }}
                      >
                        {t(`${TRANSLATION_KEY}.fullName-en`)}
                      </CustomFormLabel>
                      <Controller
                        name={`groupMembers.${idx}.fullName` as const}
                        control={formData.control}
                        render={({ field: inputField }) => (
                          <Input
                            {...inputField}
                            fullWidth
                            disabled
                            testId={`groupMembers-fullName-${idx}`}
                            placeholder={t(`${TRANSLATION_KEY}.fullName-en`)}
                            error={Boolean(memberErrors?.fullName)}
                          />
                        )}
                      />
                      {memberErrors?.fullName?.message && (
                        <FormHelperText error sx={{ ml: 0 }}>
                          {String(memberErrors.fullName.message)}
                        </FormHelperText>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </FormSection>
      </FormControl>
    </FormProvider>
  );
};

export default GroupMembers;
